<template>
  <div v-if="banners.length" class="balance-banners">
    <v-alert
      v-for="banner in banners"
      :key="banner.kind"
      :color="look[banner.kind].color"
      :variant="look[banner.kind].variant"
      :icon="look[banner.kind].icon"
      :role="QUIET.includes(banner.kind) ? 'status' : 'alert'"
      density="compact"
      rounded="0"
      class="balance-banners__item"
    >
      <div class="balance-banners__row">
        <p class="balance-banners__text">
          <template v-if="banner.kind === 'low'">
            {{
              t('billing.banners.low', {
                days: banner.runwayDays,
                rate: priceExclTax(banner.spentPerDayCents),
              })
            }}
          </template>
          <template v-else-if="banner.kind === 'critical'">
            <strong>{{ t('billing.banners.critical.title', banner.runwayDays) }}</strong>
            {{ t('billing.banners.critical.text') }}
            <template v-if="banner.incomingCents">
              {{ t('billing.banners.incoming', { amount: price(banner.incomingCents) }) }}
            </template>
          </template>
          <template v-else-if="banner.kind === 'empty' || banner.kind === 'deletion'">
            <strong>{{ t('billing.banners.empty.title') }}</strong>
            {{ t('billing.banners.empty.text') }}
            <template v-if="banner.kind === 'deletion'">
              {{ t('billing.banners.deletion.text', { date: formatDay(banner.deletableOn) }) }}
            </template>
            <template v-if="banner.incomingCents">
              {{ t('billing.banners.incoming', { amount: price(banner.incomingCents) }) }}
            </template>
          </template>
          <template v-else-if="banner.kind === 'dailyAllowance'">
            <strong>{{ t('billing.banners.dailyAllowance.title') }}</strong>
            {{
              banner.allowanceBytes === null
                ? t('billing.banners.dailyAllowance.textUnquoted')
                : t('billing.banners.dailyAllowance.text', {
                    allowance: formatBilledSize(banner.allowanceBytes, 1, locale),
                  })
            }}
          </template>
          <template v-else-if="banner.kind === 'confirmation'">
            {{ t('billing.banners.confirmation.text', { amount: priceExclTax(banner.amountExclTaxCents) }) }}
          </template>
          <template v-else-if="banner.kind === 'autoTopUpOff'">
            {{ t('billing.banners.autoTopUpOff.text', { n: banner.failures }) }}
          </template>
          <template v-else-if="banner.kind === 'deliveryNear'">
            {{
              t('billing.banners.deliveryNear.text', {
                used: formatBilledSize(banner.usedBytes, 1, locale),
                included: formatBilledSize(banner.includedBytes, 1, locale),
                rate: rateOrOffered(deliveryRateCents(book), t('billing.pricing.units.bandwidth')),
              })
            }}
          </template>
          <template v-else-if="banner.kind === 'deliveryExceeded'">
            {{
              t('billing.banners.deliveryExceeded.text', {
                included: formatBilledSize(banner.includedBytes, 1, locale),
                billed: formatBilledSize(banner.billableBytes, 1, locale),
                amount: priceExclTax(banner.billedCents),
              })
            }}
          </template>
          <template v-else>
            {{
              banner.reason === 'daily_allowance'
                ? t('billing.banners.member.dailyAllowance')
                : t('billing.banners.member.text')
            }}
            <strong>{{ t('billing.banners.member.contact') }}</strong>
          </template>
        </p>

        <div class="balance-banners__actions">
          <template v-if="banner.kind === 'low'">
            <v-btn variant="text" :color="look.low.color" class="font-weight-bold" @click="openTopUp">
              {{ t('billing.topUp.open') }}
            </v-btn>
            <v-btn variant="text" :color="look.low.color" :to="{ name: 'settings:billing' }">
              {{ t('billing.banners.autoTopUp') }}
            </v-btn>
          </template>
          <!-- A debit on its way is the top-up already: a second one would charge twice. -->
          <v-btn
            v-else-if="(banner.kind === 'critical' || banner.kind === 'empty') && !banner.incomingCents"
            variant="flat"
            :color="look[banner.kind].color"
            @click="openTopUp"
          >
            {{ t('billing.topUp.open') }}
          </v-btn>
          <!-- A first top-up is what lifts the day's allowance. -->
          <v-btn v-else-if="banner.kind === 'dailyAllowance'" variant="flat" color="warning" @click="openTopUp">
            {{ t('billing.topUp.open') }}
          </v-btn>
          <v-btn
            v-else-if="banner.kind === 'deletion' && !banner.incomingCents"
            variant="flat"
            color="error"
            @click="openTopUp"
          >
            {{ t('billing.banners.deletion.action') }}
          </v-btn>
          <v-btn
            v-else-if="banner.kind === 'confirmation'"
            variant="flat"
            color="info"
            :to="{ name: 'settings:billing-history', query: { pay: banner.purchaseId } }"
          >
            {{ t('billing.banners.confirmation.action') }}
          </v-btn>
          <v-btn
            v-else-if="banner.kind === 'autoTopUpOff'"
            variant="text"
            color="warning"
            class="font-weight-bold"
            :to="{ name: 'settings:billing' }"
          >
            {{ t('billing.banners.autoTopUpOff.action') }}
          </v-btn>

          <v-btn
            v-else-if="banner.kind === 'deliveryNear' || banner.kind === 'deliveryExceeded'"
            variant="text"
            :color="look[banner.kind].color"
            class="font-weight-bold"
            :to="{ name: 'settings:billing-usage' }"
          >
            {{ t('billing.banners.seeUsage') }}
          </v-btn>

          <v-btn
            v-if="DISMISSIBLE.includes(banner.kind)"
            :icon="mdiClose"
            :aria-label="t('billing.banners.dismiss')"
            variant="text"
            size="small"
            @click="dismiss(banner.kind)"
          />
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script setup lang="ts">
/**
 * The balance banners, at the top of every screen of the app shell (`docs/plans/billing-ui.md`
 * § Wave 2, items 1.1, 1.2, 7.3, 7.4 and U6). **What shows is `banners.ts`'s decision**; this
 * component reads the state it needs and draws the answer.
 *
 * - **A billing manager on a selling platform** gets the wallet's banners, each with its remedy:
 *   the top-up modal, the automatic top-up's settings, or the top-up waiting on the bank.
 * - **Anybody else** reads no wallet — the routes refuse them — and gets the neutral banner off
 *   `GET /access`'s `org.public_side_offline`, the visitor routes' own gate: the public side is
 *   offline, and a billing manager holds the remedy.
 * - **Why it is offline** is `org.public_side_offline_reason`: an empty wallet, or a wallet that has
 *   never paid and whose day of public delivery is spent — which reopens tomorrow and which a
 *   first top-up lifts, so the banner says that rather than « top up to come back ».
 *
 * **While a direct debit is being collected** the shortage banners say its credits are coming and
 * offer no top-up, and the low one is not drawn (`banners.ts`).
 *
 * The low banner and the member's can be dismissed **for the session**, per organization; the
 * critical, empty and deletion banners cannot, because each describes something already
 * happening or about to. The two delivery banners (§ Wave 3, 5.1) can, per organization and per
 * month: 80 % of the free delivery used, then the allowance exceeded and what it has cost.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiClockOutline,
  mdiClose,
  mdiCreditCardOffOutline,
  mdiDeleteClockOutline,
  mdiInformationOutline,
  mdiLockOutline,
  mdiSpeedometer,
  mdiSpeedometerSlow,
  mdiTimerSandEmpty,
} from '@mdi/js'
import { formatBilledSize } from '@/apps/core/utils/formatMeasure'
import { USAGE_SKU } from '@/api/usage/usage'
import { useModal } from '@/apps/core'
import { useSessionStore } from '@/apps/session/stores/session'
import {
  useAutoTopUp,
  useCreditPurchases,
  useUsagePeriods,
  useWallet,
} from '../queries'
import { currentDelivery, deliveryRateCents, isoDay } from '../delivery'
import { useMoney } from '../composables/useMoney'
import { usePrices } from '../composables/usePrices'
import { balanceBanners, type BalanceBannerKind } from '../banners'
import TopUpModal from '../modals/TopUpModal.vue'

const { t, locale } = useI18n()
const { price, priceExclTax, rateOrOffered } = useMoney()
const { open } = useModal()
const session = useSessionStore()

const managesWallet = computed(
  () => session.can('tenancy.billing.manage') && session.sellsCredits(),
)

const wallet = useWallet()
const autoTopUp = useAutoTopUp()
// Read only while the automatic top-up is on: a top-up waiting on the bank is the sweep's, and the
// sweep opens nothing while the arrangement is off.
const purchases = useCreditPurchases(
  { limit: 5 },
  { enabled: () => autoTopUp.data.value?.enabled === true },
)
// The month's delivery, for the two allowance banners — a billing manager's, on a selling platform.
const deliveryPeriods = useUsagePeriods({ sku: USAGE_SKU.BANDWIDTH }, { enabled: managesWallet })
const delivery = computed(() =>
  managesWallet.value ? currentDelivery(deliveryPeriods.data.value, isoDay(new Date())) : null,
)
// The organization's delivery rate, past the allowance — its own where negotiated.
const { book } = usePrices()

const look: Record<
  BalanceBannerKind,
  { color: string; variant: 'tonal' | 'outlined'; icon: string }
> = {
  low: { color: 'warning', variant: 'tonal', icon: mdiClockOutline },
  critical: { color: 'warning', variant: 'tonal', icon: mdiAlertOutline },
  empty: { color: 'error', variant: 'tonal', icon: mdiAlertCircleOutline },
  dailyAllowance: { color: 'warning', variant: 'tonal', icon: mdiTimerSandEmpty },
  deletion: { color: 'error', variant: 'outlined', icon: mdiDeleteClockOutline },
  confirmation: { color: 'info', variant: 'tonal', icon: mdiLockOutline },
  autoTopUpOff: { color: 'warning', variant: 'tonal', icon: mdiCreditCardOffOutline },
  member: { color: 'grey', variant: 'tonal', icon: mdiInformationOutline },
  deliveryNear: { color: 'info', variant: 'tonal', icon: mdiSpeedometerSlow },
  deliveryExceeded: { color: 'warning', variant: 'tonal', icon: mdiSpeedometer },
}

/** The banners that inform rather than alarm, announced politely. */
const QUIET: readonly BalanceBannerKind[] = ['low', 'deliveryNear', 'deliveryExceeded']

// ------------------------------------------------------------------ the session's dismissals

const DISMISSIBLE: readonly BalanceBannerKind[] = [
  'low',
  'member',
  'deliveryNear',
  'deliveryExceeded',
]

/**
 * A delivery banner's dismissal is keyed to its period as well, so next month's shows again: once
 * a month each.
 */
function storageKey(kind: BalanceBannerKind): string {
  const org = session.access?.org?.org_id ?? 'none'
  const month = kind.startsWith('delivery')
    ? `.${delivery.value?.periodStart ?? 'none'}`
    : ''
  return `videas.billing.banner.${kind}.${org}${month}`
}

/** Bumped on a dismissal, so the computed below reads the storage again. */
const dismissals = ref(0)

const dismissed = computed(() => {
  void dismissals.value
  const kinds = new Set<BalanceBannerKind>()
  for (const kind of DISMISSIBLE) {
    try {
      if (sessionStorage.getItem(storageKey(kind)) === '1') kinds.add(kind)
    } catch {
      // A browser that refuses the storage shows the banner again on the next screen.
    }
  }
  return kinds
})

function dismiss(kind: BalanceBannerKind): void {
  try {
    sessionStorage.setItem(storageKey(kind), '1')
  } catch {
    // Dismissed for this mount only.
  }
  dismissals.value += 1
  hiddenNow.value.add(kind)
}

/** What was dismissed while the storage refused to remember it. */
const hiddenNow = ref(new Set<BalanceBannerKind>())

// -------------------------------------------------------------------------------- the list

const banners = computed(() =>
  balanceBanners({
    managesWallet: managesWallet.value,
    sellsCredits: session.sellsCredits(),
    publicSideOffline: session.publicSideOffline(),
    publicSideOfflineReason: session.publicSideOfflineReason(),
    dailyDeliveryAllowanceBytes: session.dailyDeliveryAllowanceBytes(),
    wallet: managesWallet.value ? wallet.data.value : null,
    autoTopUp: managesWallet.value ? autoTopUp.data.value : null,
    purchases: purchases.data.value?.pages[0]?.data ?? [],
    delivery: delivery.value,
    prices: book.value,
    dismissed: new Set([...dismissed.value, ...hiddenNow.value]),
  }),
)

async function openTopUp(): Promise<void> {
  await open(t('billing.topUp.title'), TopUpModal, { width: 560, maxWidth: 560, persistent: true })
  await Promise.all([wallet.refetch(), autoTopUp.refetch()])
}

/** A `YYYY-MM-DD` day, as the reader's language writes it — read as that day, in no time zone. */
function formatDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.balance-banners {
  display: flex;
  flex-direction: column;

  &__item {
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-sm $space-md;
  }

  &__text {
    flex: 1 1 360px;
    margin: 0;
    font-size: $text-sm;
    line-height: $leading-normal;

    strong {
      font-weight: $font-semibold;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-xs;
  }
}
</style>
